import React, { useState, useEffect } from "react";
import { useGetGroupImagesQuery } from "../../../../../services/groupAPI";
import { mapObjectToArray } from "../../../../../utils/GroupNotificationUtils";
import { imageNotFoundURL } from "./utils";
import { LoadingSpinner, ErrorDisplay } from "../../../../../utils/StateDisplay";
import { LanguageText } from "../../../../../dictionary/ChartsText";
import useGetLanguage from "../../../../../utils/useGetLanguage";
export interface GroupImage {
  image_id: string;
  content_type: string;
  description: string;
  url: string;
}

export enum Status {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  ACKNOWLEDGE = "Acknowledge",
}

interface GroupImageSelectProps {
  status: Status;
  groupID: string;
  currentImageID?: string | "";
  onImageSelect: (imageID: string) => void;
}

const GroupImageSelect: React.FC<GroupImageSelectProps> = ({
  status,
  groupID,
  currentImageID = "",
  onImageSelect,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "ChartsText",
  });

  const [selectedImageID, setSelectedImageID] = useState<string | "">(currentImageID);
  const [groupImages, setGroupImages] = useState<GroupImage[]>([]);

  const {
    data: groupImageData,
    isLoading,
    error,
  } = useGetGroupImagesQuery({ group_id: groupID ?? "" }, { skip: !groupID });

  useEffect(() => {
    if (currentImageID) {
      setSelectedImageID(currentImageID);
    }
  }, [currentImageID]);

  useEffect(() => {
    if (groupImageData) {
      const imagesArray = mapObjectToArray(groupImageData);
      setGroupImages(imagesArray);
    }
  }, [groupImageData]);

  const handleImageSelect = (image: GroupImage) => {
    if (image.image_id !== selectedImageID) {
      setSelectedImageID(image.image_id);
      onImageSelect(image.image_id);
    }
  };

  const handleReset = () => {
    setSelectedImageID("");
    onImageSelect("");
  };

  if (isLoading)
    return (
      <LoadingSpinner message={language?.charts?.map?.markers?.notification?.loading} />
    );
  if (error)
    return (
      <ErrorDisplay
        message={language?.charts?.map?.markers?.notification?.errorMessages?.error}
      />
    );

  return (
    <div className="relative w-full max-w-xs p-4 mb-4 bg-white shadow-lg rounded-lg flex flex-col items-center space-y-4">
      {selectedImageID !== "" && (
        <button
          onClick={handleReset}
          className="absolute top-0 right-0 p-1 bg-white rounded-full shadow-lg border border-gray-300 text-gray-700"
          aria-label="Reset to Default Image"
          title="Reset Image"
        >
          <span className="text-xl">🔄</span>
        </button>
      )}

      <h4 className="text-sm font-semibold text-center">
        {status === Status.ACTIVE
          ? language?.charts?.map?.markers?.notification?.titles?.activeImage
          : status === Status.INACTIVE
          ? language?.charts?.map?.markers?.notification?.titles?.inactiveImage
          : "Acknowledge"}
      </h4>

      <div className="w-full flex justify-center mb-4">
        <div className="relative w-48 h-48 flex justify-center items-center border-2 border-gray-300 rounded-lg">
          {selectedImageID ? (
            <img
              src={
                groupImages.find((image) => image.image_id === selectedImageID)?.url ||
                imageNotFoundURL
              }
              alt={
                groupImages.find((image) => image.image_id === selectedImageID)
                  ?.description || "Image Not Found"
              }
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-full flex justify-center items-center bg-gray-200 rounded-lg">
              <span className="text-gray-500">📸</span>
            </div>
          )}
        </div>
      </div>

      {groupImages.length === 0 ? (
        <div className="w-full flex items-start p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 rounded-lg">
          <svg
            className="w-6 h-6 text-yellow-500 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M12 2a10 10 0 1010 10A10 10 0 0012 2z"
            />
          </svg>
          <div className="ml-3">
            <h3 className="font-semibold">Attention</h3>
            <p className="text-sm">
              No images were found in your group. A default system image will be used!
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 w-full max-h-[40vh] overflow-auto">
          {groupImages.map((image) => (
            <div
              key={image.image_id}
              className={`cursor-pointer flex flex-col items-center p-2 bg-white border rounded-lg shadow-sm transition-all duration-300 ease-in-out ${
                selectedImageID === image.image_id
                  ? "border-4 border-blue-500"
                  : "border-2 border-gray-300"
              }`}
              onClick={() => handleImageSelect(image)}
            >
              <div className="w-full aspect-square bg-gray-100 rounded-md overflow-hidden flex items-center justify-center">
                <img
                  src={image.url}
                  alt={image.description}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="mt-2 text-xs sm:text-sm font-medium text-gray-900 truncate w-full text-center">
                <span className="block truncate">{image.description || ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupImageSelect;
